import { FileType, PodcastMediaType } from "@src/gql/generated";
import { MediaModule } from "@src/utils/mediaModule";
import { uploadLargeFileInChunks } from "@src/utils/helpers/upload-post/multipartUpload";
import { OtherMediaUpload } from "@src/utils/helpers/uploadOtherMedia";
import { withResolvedMimeType } from "@src/utils/helpers/resolveUploadMimeType";

export const PODCAST_AUDIO_ACCEPT = "audio/*,.mp3,.m4a,.aac,.wav,.ogg,.flac";
export const PODCAST_VIDEO_ACCEPT = "video/mp4,video/quicktime,.mp4,.mov,.m4v";
export const PODCAST_MAX_FILE_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB — audio only
export const PODCAST_COVER_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Podcast video publishes straight from the raw upload — there's no transcoding step — and
 * an episode uploaded once is played back on web, iOS, and Android alike, so only containers
 * that decode natively EVERYWHERE are allowed. WebM has no decoder in iOS's AVPlayer (which
 * expo-video uses) and is patchy in Safari; AVI/MKV/etc. have no browser or mobile support at
 * all. All of these upload fine but then silently fail to play, so they're rejected here
 * before the upload even starts.
 */
const ALLOWED_VIDEO_EXTENSIONS = new Set(["mp4", "mov", "m4v"]);
const UNSUPPORTED_VIDEO_LABELS: Record<string, string> = {
  avi: "AVI",
  mkv: "MKV",
  wmv: "WMV",
  flv: "FLV",
  webm: "WebM",
  "3gp": "3GP",
};

/**
 * Podcast media always goes through the generic multipart pipeline, regardless of size.
 * `uploadMedia` (the single-request path) only accepts POST_IMAGE / POST_VIDEO / DOCUMENT,
 * and a one-part multipart upload works fine for small files, so there is one transport.
 */
export async function uploadPodcastMedia(
  file: File,
  mediaType: PodcastMediaType,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<{ fileUrl: string }> {
  const result = await uploadLargeFileInChunks({
    file: withResolvedMimeType(file),
    fileType: mediaType === PodcastMediaType.Audio ? FileType.PodcastAudio : FileType.PodcastVideo,
    module: MediaModule.Podcast,
    onProgress,
    signal,
  });

  if (!result.fileUrl) throw new Error("Upload finished without a file URL");
  return { fileUrl: result.fileUrl };
}

/** Cover art is a small image — reuse the existing post-image upload path. */
export async function uploadPodcastCover(file: File): Promise<{ fileUrl: string }> {
  const result = await OtherMediaUpload(file, undefined, false, MediaModule.Podcast);
  if (!result.fileUrl) throw new Error("Cover upload finished without a file URL");
  return { fileUrl: result.fileUrl };
}

export function validatePodcastFile(file: File, mediaType: PodcastMediaType): string | null {
  const mime = withResolvedMimeType(file).type;
  const expected = mediaType === PodcastMediaType.Audio ? "audio/" : "video/";
  if (!mime.startsWith(expected)) {
    return mediaType === PodcastMediaType.Audio ? "Please choose an audio file." : "Please choose a video file.";
  }
  if (mediaType === PodcastMediaType.Video) {
    const ext = file.name.toLowerCase().split(".").pop() || "";
    if (!ALLOWED_VIDEO_EXTENSIONS.has(ext)) {
      const label = UNSUPPORTED_VIDEO_LABELS[ext] || ext.toUpperCase();
      return `${label} videos won't play correctly on every device. Please upload an MP4 or MOV file instead.`;
    }
  }
  // Video has no size cap; the 5 GB cap applies to audio only.
  if (mediaType === PodcastMediaType.Audio && file.size > PODCAST_MAX_FILE_BYTES) {
    return "File is too large (max 5 GB).";
  }
  return null;
}

/** Reads duration from file metadata in the browser; resolves undefined if unreadable. */
export function readMediaDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(file.type.startsWith("video/") ? "video" : "audio");
    const done = (value?: number) => {
      URL.revokeObjectURL(url);
      el.removeAttribute("src");
      resolve(value);
    };
    el.preload = "metadata";
    el.onloadedmetadata = () =>
      done(Number.isFinite(el.duration) && el.duration > 0 ? Math.round(el.duration) : undefined);
    el.onerror = () => done(undefined);
    el.src = url;
  });
}
